import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { InboxOutlined, ExperimentOutlined } from "@ant-design/icons";
import { getSpecsData, getProductFields } from "../../../../actions/specManager";
import {
  parseSpecDetail,
  formatSpec,
  buildIdentifierFields,
  SPEC_MANAGER_TAB_KEYS,
  SPEC_FILTER_VALUES,
  canViewSpecManager,
} from "../utils/utils";

import { DiscardChangesModal } from "../shared/specManagerUi";
import ProductSpecListPanel from "./ProductSpecListPanel";
import ProductSpecEditor from "./ProductSpecEditor";
import TestSpecManager from "../testSpec/TestSpecManager";
import "../SpecManager.css";

/** Set to true to show Product Spec / Test Spec tab switches in the Product Spec Manager. */
const SHOW_SPEC_MANAGER_TABS = false;

const TAB_CONFIG = [
  { key: SPEC_MANAGER_TAB_KEYS.PRODUCT, label: "Product Spec", icon: <InboxOutlined /> },
  { key: SPEC_MANAGER_TAB_KEYS.TEST, label: "Test Spec", icon: <ExperimentOutlined /> },
];

export default function ProductSpecManager({ customer, onEditModeChange }) {
  const [identifierFields, setIdentifierFields] = useState([]);
  const [activeTab, setActiveTab] = useState(SPEC_MANAGER_TAB_KEYS.TEST);
  const [productRows, setProductRows] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [productSearchField, setProductSearchField] = useState("");
  const [activeFilter, setActiveFilter] = useState(SPEC_FILTER_VALUES.ALL);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPageLink, setNextPageLink] = useState("");
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAddingSpec, setIsAddingSpec] = useState(false);
  const [specRows, setSpecRows] = useState([]);
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [specDataChanged, setSpecDataChanged] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [isEditingTestSpec, setIsEditingTestSpec] = useState(false);
  const [isProductFieldsReady, setIsProductFieldsReady] = useState(false);

  const abortControllerRef = useRef(null);

  const fetchProductList = async ({
    filter = activeFilter,
    search = searchValue,
    searchField = productSearchField,
    page = "1",
    append = false,
    scrollReset = false,
  } = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    const response = await getSpecsData({
      filter,
      search,
      search_field: searchField,
      product_id: "",
      page,
    }, false, abortController.signal, customer);

    if (response.canceled) return;

    if (response.success) {
      setProductRows((previous) => (append ? [...previous, ...response.files] : response.files));
      setNextPageLink(response.pages.nextPageLink);
    } else if (!append) {
      setProductRows([]);
      setNextPageLink("");
      toast.error("Failed to get spec data.");
    }

    setLoading(false);
    setLoadingMore(false);
    if (scrollReset) {
      setSearchValue(search);
      setActiveFilter(filter);
    }
  };

  useEffect(() => {
    let canceled = false;

    setIdentifierFields([]);
    setProductSearchField("");
    setSearchValue("");
    setActiveFilter(SPEC_FILTER_VALUES.ALL);
    setProductRows([]);
    setNextPageLink("");
    setLoading(true);
    setIsProductFieldsReady(false);

    getProductFields(null, customer).then((result) => {
      if (canceled) return;

      if (!result.success) {
        if (!result.canceled && result.message) {
          if (result.isWarning) {
            toast.warning(result.message);
          } else {
            toast.error(result.message);
          }
        }
        setLoading(false);
        return;
      }

      const fields = buildIdentifierFields(result);
      const defaultSearchField = fields[0].json_field;

      setIdentifierFields(fields);
      setProductSearchField(defaultSearchField);
      setIsProductFieldsReady(true);
      if (SHOW_SPEC_MANAGER_TABS && activeTab === SPEC_MANAGER_TAB_KEYS.PRODUCT) {
        fetchProductList({ scrollReset: true, searchField: defaultSearchField });
      } else {
        setLoading(false);
      }
    });

    return () => {
      canceled = true;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [customer.company, customer.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(isEditingProduct || isEditingTestSpec);
    }
  }, [isEditingProduct, isEditingTestSpec, onEditModeChange]);

  const handleTabChange = (tabKey) => {
    if (tabKey === activeTab) return;
    setActiveTab(tabKey);
    if (tabKey === SPEC_MANAGER_TAB_KEYS.PRODUCT) {
      fetchProductList({ scrollReset: true });
    }
  };

  const handleProductRowClick = async (event, rowIndex, status) => {
    event.preventDefault();
    const productRow = productRows[rowIndex];
    setSelectedProduct(productRow);
    setIsAddingSpec(status === "pending");
    setIsEditingProduct(true);
    setLoadingEditData(true);
    setSpecDataChanged(false);

    const response = await getSpecsData({
      filter: "",
      search: "",
      search_field: productSearchField,
      product_id: productRow.product_id,
      page: "1",
    }, false, null, customer);

    if (response.success && response.files) {
      const parsedRows = response.files.map((specRow) => parseSpecDetail(specRow));
      setSpecRows(parsedRows);
    } else {
      toast.error("Failed to get spec data.");
      setIsEditingProduct(false);
    }
    setLoadingEditData(false);
  };

  const handleSaveSpec = async (formattedSpecs) => {
    const response = await getSpecsData({
      product_id: selectedProduct.product_id,
      specs: formattedSpecs,
      filter: "",
      search: "",
      search_field: productSearchField,
      page: "1",
    }, true, null, customer);

    if (response.success) {
      toast.success("Specs and status updated.");
      await fetchProductList();
      return true;
    }
    toast.error("Failed to save specs.");
    return false;
  };

  const handleSaveTestList = async (updatedSpecRows) => {
    const formattedSpecs = formatSpec(updatedSpecRows);
    if (formattedSpecs.some((item) => typeof item === "string")) {
      toast.error(formattedSpecs.find((item) => typeof item === "string"));
      return false;
    }
    const response = await getSpecsData({
      product_id: selectedProduct.product_id,
      specs: formattedSpecs,
      action: "update_test_list",
      filter: "",
      search: "",
      search_field: productSearchField,
      page: "1",
    }, true, null, customer);

    if (response.success) {
      toast.success("Test list updated.");
      return true;
    }
    toast.error("Failed to update test list.");
    return false;
  };

  const exitProductEditor = () => {
    setIsEditingProduct(false);
    setSelectedProduct(null);
    setSpecRows([]);
    setSpecDataChanged(false);
    setIsAddingSpec(false);
  };

  const handleBackFromEditor = () => {
    if (specDataChanged) {
      setPendingCloseAction(() => exitProductEditor);
      setShowDiscardModal(true);
    } else {
      exitProductEditor();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  const handleLoadMore = () => {
    if (!nextPageLink || loadingMore || loading) return;
    const page = nextPageLink.split("page=")[1].split("&")[0];
    fetchProductList({ page, append: true });
  };

  if (!canViewSpecManager()) {
    return null;
  }

  const showProductSpec = SHOW_SPEC_MANAGER_TABS
    && activeTab === SPEC_MANAGER_TAB_KEYS.PRODUCT;
  const showTestSpec = !SHOW_SPEC_MANAGER_TABS
    || activeTab === SPEC_MANAGER_TAB_KEYS.TEST;

  return (
    <div className="SpecManager_Root">
      {SHOW_SPEC_MANAGER_TABS && !isEditingProduct && !isEditingTestSpec && (
        <div className="SpecManager_Tabs">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`SpecManager_TabButton${activeTab === tab.key ? " SpecManager_TabButton--active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {showProductSpec && isProductFieldsReady && isEditingProduct && (
        <ProductSpecEditor
          customer={customer}
          identifierFields={identifierFields}
          productRow={selectedProduct}
          isAddingSpec={isAddingSpec}
          specRows={specRows}
          loading={loadingEditData}
          specDataChanged={specDataChanged}
          onSpecRowsChange={setSpecRows}
          onSpecDataChanged={setSpecDataChanged}
          onSaveSpec={handleSaveSpec}
          onSaveTestList={handleSaveTestList}
          onBack={handleBackFromEditor}
        />
      )}

      {showProductSpec && isProductFieldsReady && !isEditingProduct && (
        <ProductSpecListPanel
          productRows={productRows}
          identifierFields={identifierFields}
          loading={loading}
          loadingMore={loadingMore}
          searchValue={searchValue}
          searchField={productSearchField}
          activeFilter={activeFilter}
          onSearchChange={(value) => {
            setSearchValue(value);
            fetchProductList({ search: value, scrollReset: true });
          }}
          onSearchFieldChange={(field) => {
            setProductSearchField(field);
            fetchProductList({ search: searchValue, searchField: field, scrollReset: true });
          }}
          onFilterChange={(filter) => {
            setActiveFilter(filter);
            fetchProductList({ filter, scrollReset: true });
          }}
          onProductRowClick={handleProductRowClick}
          onLoadMore={handleLoadMore}
        />
      )}

      {showTestSpec && (
        <TestSpecManager
          customer={customer}
          identifierFields={identifierFields}
          onEditModeChange={setIsEditingTestSpec}
          onDataRefresh={() => fetchProductList({ scrollReset: true })}
        />
      )}

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={() => {
          setShowDiscardModal(false);
          setPendingCloseAction(null);
        }}
        onConfirm={handleDiscardConfirm}
      />
    </div>
  );
}
